import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import {
  EnterpriseModal,
  ModalPrimaryButton,
  ModalSecondaryButton,
  modalInputClass,
  modalSelectClass,
} from '../layout/EnterpriseModal';
import { EnterpriseBadge } from '../layout/EnterpriseTable';
import type { OrgMember } from '../../hooks/useOrgMembers';
import {
  useDeleteOpportunity,
  useLinkOpportunityAction,
  useLinkOpportunityKaizen,
  useLinkOpportunityProject,
  useOpportunity,
  useOpportunityOutcomes,
  useUnlinkOpportunityOutcome,
  useUpdateOpportunity,
} from '../../hooks/useOpportunities';
import { NewActionModal } from '../../modules/actions/components/NewActionModal';
import KaizenForm from '../KaizenForm';
import { ACTION_STATUS_LABELS, type ActionStatus } from '../../services/actionService';
import { ProjectService } from '../../services/projectService';
import type { OpportunityOutcomeDetail, OpportunityOutcomeKind } from '../../services/opportunityService';
import { PROJECT_STATUS_LABELS, type ProjectStatus } from '../../types/enterpriseProject';
import { KAIZEN_STATUS_LABELS } from '../../utils/problemKaizenLink';
import type { KaizenStatus } from '../../types/gainsMonitor';
import {
  DERIVED_PRIORITY_BADGE,
  DERIVED_PRIORITY_LABELS,
  OPPORTUNITY_LEVELS,
  OPPORTUNITY_LEVEL_LABELS,
  OPPORTUNITY_SOURCE_LABELS,
  OPPORTUNITY_STATUS_BADGE,
  OPPORTUNITY_STATUS_LABELS,
  categoryLabel,
  deriveOpportunityPriority,
  opportunityDecisions,
  usefulSourceContext,
  type OpportunityLevel,
} from '../../types/opportunity';

interface OpportunityDetailDrawerProps {
  opportunityId: string | null;
  organizationId: string;
  members: OrgMember[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
  onError: (message: string) => void;
  onActionLinked: () => void;
  onProjectLinked: () => void;
  onKaizenLinked: () => void;
}

interface Draft {
  title: string;
  description: string;
  category: string;
  ownerId: string;
  impact: OpportunityLevel | '';
  effort: OpportunityLevel | '';
}

const fieldClass = modalInputClass;
const labelClass = 'mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400';

function outcomeStatusLabel(kind: OpportunityOutcomeKind, status: string | null): string {
  if (!status) return '—';
  if (kind === 'action') return ACTION_STATUS_LABELS[status as ActionStatus] ?? status;
  if (kind === 'kaizen') return KAIZEN_STATUS_LABELS[status as KaizenStatus] ?? status;
  return PROJECT_STATUS_LABELS[status as ProjectStatus] ?? status;
}

const OUTCOME_GROUP_LABEL: Record<OpportunityOutcomeKind, string> = {
  action: 'Ações',
  kaizen: 'Kaizens',
  project: 'Projetos',
};

export const OpportunityDetailDrawer: React.FC<OpportunityDetailDrawerProps> = ({
  opportunityId,
  organizationId,
  members,
  onClose,
  onSaved,
  onDeleted,
  onError,
  onActionLinked,
  onProjectLinked,
  onKaizenLinked,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const detailQuery = useOpportunity(organizationId, opportunityId);
  const outcomesQuery = useOpportunityOutcomes(organizationId, opportunityId);
  const updateMutation = useUpdateOpportunity(organizationId);
  const deleteMutation = useDeleteOpportunity(organizationId);
  const linkMutation = useLinkOpportunityAction(organizationId);
  const linkProjectMutation = useLinkOpportunityProject(organizationId);
  const linkKaizenMutation = useLinkOpportunityKaizen(organizationId);
  const unlinkMutation = useUnlinkOpportunityOutcome(organizationId);
  const opportunity = detailQuery.data ?? null;
  const outcomes = outcomesQuery.data ?? [];
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [unfoldOpen, setUnfoldOpen] = useState(false);
  const [actionStep, setActionStep] = useState(false);
  const [kaizenStep, setKaizenStep] = useState(false);
  const [kaizenFormOpen, setKaizenFormOpen] = useState(false);
  const [pendingKaizenId, setPendingKaizenId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [pendingProjectId, setPendingProjectId] = useState<string | null>(null);
  const [projectFormOpen, setProjectFormOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<OpportunityOutcomeDetail | null>(null);

  const projectsQuery = useQuery({
    queryKey: ['enterprise-projects-list', organizationId],
    queryFn: () => ProjectService.list(organizationId),
    enabled: Boolean(organizationId) && (unfoldOpen || actionModalOpen),
    staleTime: 60_000,
  });
  const visibleProjects = projectsQuery.data ?? [];

  useEffect(() => {
    setDraft(null);
    setConfirmDelete(false);
    setUnfoldOpen(false);
    setActionStep(false);
    setKaizenStep(false);
    setKaizenFormOpen(false);
    setSelectedProjectId('');
    setActionModalOpen(false);
    setPendingActionId(null);
    setPendingProjectId(null);
    setPendingKaizenId(null);
    setProjectFormOpen(false);
    setUnlinkTarget(null);
  }, [opportunityId]);

  useEffect(() => {
    if (!opportunity || opportunity.id !== opportunityId || draft) return;
    setDraft({
      title: opportunity.title,
      description: opportunity.description ?? '',
      category: opportunity.category === 'other' ? '' : opportunity.category,
      ownerId: opportunity.owner_id ?? '',
      impact: opportunity.impact_level ?? '',
      effort: opportunity.effort_level ?? '',
    });
  }, [draft, opportunity, opportunityId]);

  if (!opportunityId) return null;

  const readOnly = opportunity?.status === 'realized';
  const pending = updateMutation.isPending || deleteMutation.isPending || linkMutation.isPending || linkProjectMutation.isPending || linkKaizenMutation.isPending || unlinkMutation.isPending || creatingProject;
  const priority = deriveOpportunityPriority(
    draft ? (draft.impact || null) : opportunity?.impact_level,
    draft ? (draft.effort || null) : opportunity?.effort_level,
  );
  const decisions = opportunity ? opportunityDecisions(opportunity.status) : [];
  const contextEntries = opportunity && opportunity.source_kind !== 'manual'
    ? usefulSourceContext(opportunity.source_context)
    : [];

  const handleSave = async () => {
    if (!opportunity || !draft || readOnly) return;
    try {
      await updateMutation.mutateAsync({
        id: opportunity.id,
        input: {
          title: draft.title,
          description: draft.description,
          category: draft.category,
          owner_id: draft.ownerId || null,
          impact_level: draft.impact || null,
          effort_level: draft.effort || null,
        },
      });
      onSaved();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível atualizar a oportunidade.');
    }
  };

  const handleStatus = async (status: typeof decisions[number]['status']) => {
    if (!opportunity || readOnly) return;
    try {
      await updateMutation.mutateAsync({ id: opportunity.id, input: { status } });
      onSaved();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível atualizar a oportunidade.');
    }
  };

  const handleDelete = async () => {
    if (!opportunity) return;
    try {
      await deleteMutation.mutateAsync(opportunity.id);
      setConfirmDelete(false);
      onDeleted();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível remover a oportunidade.');
    }
  };

  const attachAction = async (actionId: string) => {
    if (!opportunity) return;
    try {
      await linkMutation.mutateAsync({ opportunityId: opportunity.id, actionId });
      setPendingActionId(null);
      onActionLinked();
    } catch {
      setPendingActionId(actionId);
    }
  };

  const attachProject = async (projectId: string) => {
    if (!opportunity) return;
    try {
      await linkProjectMutation.mutateAsync({ opportunityId: opportunity.id, projectId });
      setPendingProjectId(null);
      onProjectLinked();
    } catch {
      setPendingProjectId(projectId);
    }
  };

  const attachKaizen = async (kaizenId: string) => {
    if (!opportunity) return;
    try {
      await linkKaizenMutation.mutateAsync({ opportunityId: opportunity.id, kaizenId });
      setPendingKaizenId(null);
      onKaizenLinked();
    } catch {
      setPendingKaizenId(kaizenId);
    }
  };

  const handleCreateProject = async () => {
    if (!opportunity || !projectName.trim()) return;
    setCreatingProject(true);
    try {
      const userId = await ProjectService.getCurrentUserId();
      const project = await ProjectService.create(organizationId, userId, {
        name: projectName.trim(),
        description: projectDescription.trim() || undefined,
      });
      setProjectFormOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['enterprise-projects-list', organizationId] });
      await attachProject(project.id);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível criar o projeto.');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleUnlink = async () => {
    if (!unlinkTarget) return;
    try {
      await unlinkMutation.mutateAsync(unlinkTarget.id);
      setUnlinkTarget(null);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível remover o vínculo.');
    }
  };

  const openOutcome = (outcome: OpportunityOutcomeDetail) => {
    if (!outcome.accessible) return;
    if (outcome.kind === 'action') navigate(`/acoes?actionId=${outcome.target_id}`);
    if (outcome.kind === 'kaizen') navigate(`/ganhos?kaizenId=${outcome.target_id}`);
    if (outcome.kind === 'project') navigate(`/projetos/${outcome.target_id}`);
  };

  const outcomeGroups: OpportunityOutcomeKind[] = ['action', 'kaizen', 'project'];

  return (
    <div className="fixed inset-0 z-[55] flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={pending ? undefined : onClose} />
      <aside className="relative z-10 flex h-full w-full max-w-[440px] flex-col border-l border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Oportunidade</span>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Fechar">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
          {detailQuery.isLoading && <p className="text-[12px] text-slate-400">Carregando...</p>}
          {detailQuery.isError && (
            <p className="text-[12px] text-rose-600">Não foi possível abrir a oportunidade.</p>
          )}
          {detailQuery.isSuccess && !opportunity && (
            <p className="text-[12px] text-slate-500">Oportunidade não encontrada.</p>
          )}

          {opportunity && draft && (
            <>
              <section className="space-y-3">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Identificação</h3>
                <div>
                  <label className={labelClass}>Título</label>
                  <input
                    value={draft.title}
                    disabled={readOnly}
                    onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Descrição</label>
                  <textarea
                    value={draft.description}
                    disabled={readOnly}
                    onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                    className={`${fieldClass} min-h-[72px] resize-y`}
                    rows={3}
                  />
                </div>
                <div>
                  <label className={labelClass}>Categoria</label>
                  <input
                    value={readOnly ? categoryLabel(opportunity.category) : draft.category}
                    disabled={readOnly}
                    placeholder="Outra"
                    onChange={(event) => setDraft({ ...draft, category: event.target.value })}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Responsável</label>
                  <select
                    value={draft.ownerId}
                    disabled={readOnly}
                    onChange={(event) => setDraft({ ...draft, ownerId: event.target.value })}
                    className={modalSelectClass}
                  >
                    <option value="">Sem responsável</option>
                    {members.map((member) => (
                      <option key={member.id} value={member.id}>{member.name}</option>
                    ))}
                    {draft.ownerId && !members.some((member) => member.id === draft.ownerId) && (
                      <option value={draft.ownerId}>{opportunity.owner_name || 'Responsável atual'}</option>
                    )}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className={labelClass}>Status</span>
                  <EnterpriseBadge
                    label={OPPORTUNITY_STATUS_LABELS[opportunity.status]}
                    color={OPPORTUNITY_STATUS_BADGE[opportunity.status]}
                  />
                </div>
              </section>

              <section className="space-y-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Origem</h3>
                <p className="text-[12px] text-slate-800 dark:text-slate-100">
                  {OPPORTUNITY_SOURCE_LABELS[opportunity.source_kind]}
                </p>
                {opportunity.source_label ? (
                  <p className="text-[12px] text-slate-600 dark:text-slate-300">{opportunity.source_label}</p>
                ) : null}
                {opportunity.source_kind === 'problem' && opportunity.source_issue_id ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/problemas/${opportunity.source_issue_id}`)}
                    className="text-[12px] font-semibold text-blue-700 hover:underline dark:text-blue-300"
                  >
                    Abrir problema
                  </button>
                ) : null}
                {contextEntries.length > 0 && (
                  <dl className="space-y-1 rounded border border-slate-200 px-3 py-2 dark:border-slate-700">
                    {contextEntries.map(([key, value]) => (
                      <div key={key} className="grid grid-cols-[88px_1fr] gap-2 text-[11px]">
                        <dt className="truncate text-slate-400">{key}</dt>
                        <dd className="break-words text-slate-700 dark:text-slate-200">{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </section>

              <section className="space-y-3">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Avaliação</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Impacto</label>
                    <select
                      value={draft.impact}
                      disabled={readOnly}
                      onChange={(event) => setDraft({ ...draft, impact: event.target.value as OpportunityLevel | '' })}
                      className={modalSelectClass}
                    >
                      <option value="">Não informado</option>
                      {OPPORTUNITY_LEVELS.map((level) => (
                        <option key={level} value={level}>{OPPORTUNITY_LEVEL_LABELS[level]}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Esforço</label>
                    <select
                      value={draft.effort}
                      disabled={readOnly}
                      onChange={(event) => setDraft({ ...draft, effort: event.target.value as OpportunityLevel | '' })}
                      className={modalSelectClass}
                    >
                      <option value="">Não informado</option>
                      {OPPORTUNITY_LEVELS.map((level) => (
                        <option key={level} value={level}>{OPPORTUNITY_LEVEL_LABELS[level]}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                  <span>Prioridade</span>
                  <EnterpriseBadge label={DERIVED_PRIORITY_LABELS[priority]} color={DERIVED_PRIORITY_BADGE[priority]} />
                </div>
              </section>

              <section className="space-y-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Decisão</h3>
                {readOnly ? (
                  <p className="text-[12px] text-slate-500">Realizada. A decisão fica somente leitura nesta fase.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {decisions.map((decision) => (
                      <button
                        key={decision.status}
                        type="button"
                        disabled={pending}
                        onClick={() => handleStatus(decision.status)}
                        className="rounded border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        {decision.label}
                      </button>
                    ))}
                  </div>
                )}
              </section>

              <section className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Desdobramentos</h3>
                  <span className="text-[11px] text-slate-500">
                    {outcomesQuery.isLoading ? '…' : `${outcomes.length} ${outcomes.length === 1 ? 'desdobramento' : 'desdobramentos'}`}
                  </span>
                </div>

                {outcomesQuery.isError && (
                  <p className="text-[12px] text-rose-600">Não foi possível carregar os desdobramentos.</p>
                )}

                {!outcomesQuery.isLoading && outcomes.length === 0 && !pendingActionId && !pendingProjectId && !pendingKaizenId && (
                  <p className="text-[12px] text-slate-500">Nenhum desdobramento.</p>
                )}

                {outcomeGroups.map((kind) => {
                  const group = outcomes.filter((item) => item.kind === kind);
                  if (group.length === 0) return null;
                  return (
                    <div key={kind} className="space-y-1.5">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{OUTCOME_GROUP_LABEL[kind]}</p>
                      {group.map((item) => (
                        <div key={item.id} className="rounded border border-slate-200 px-2.5 py-2 dark:border-slate-700">
                          <p className="text-[12px] font-medium text-slate-800 dark:text-slate-100">
                            {item.accessible ? item.title || 'Sem título' : 'Sem acesso'}
                          </p>
                          {item.accessible && (
                            <p className="mt-0.5 text-[11px] text-slate-500">
                              {outcomeStatusLabel(item.kind, item.status)}
                              {' · '}
                              {item.owner_label || 'Sem responsável'}
                            </p>
                          )}
                          <div className="mt-1.5 flex gap-2">
                            <button
                              type="button"
                              disabled={!item.accessible}
                              onClick={() => openOutcome(item)}
                              className="text-[11px] font-medium text-blue-600 disabled:text-slate-300"
                            >
                              Abrir
                            </button>
                            <button
                              type="button"
                              onClick={() => setUnlinkTarget(item)}
                              className="text-[11px] font-medium text-rose-600"
                            >
                              Remover vínculo
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {pendingActionId && (
                  <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/40">
                    <p className="text-[12px] text-amber-800 dark:text-amber-200">
                      A ação foi criada, mas não foi possível vinculá-la à oportunidade.
                    </p>
                    <button
                      type="button"
                      disabled={linkMutation.isPending}
                      onClick={() => attachAction(pendingActionId)}
                      className="mt-2 rounded border border-amber-400 px-2 py-1 text-[11px] font-medium text-amber-900 disabled:opacity-50 dark:text-amber-100"
                    >
                      Tentar vincular novamente
                    </button>
                  </div>
                )}

                {pendingProjectId && (
                  <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/40">
                    <p className="text-[12px] text-amber-800 dark:text-amber-200">
                      O projeto foi criado, mas não foi possível vinculá-lo à oportunidade.
                    </p>
                    <button
                      type="button"
                      disabled={linkProjectMutation.isPending}
                      onClick={() => attachProject(pendingProjectId)}
                      className="mt-2 rounded border border-amber-400 px-2 py-1 text-[11px] font-medium text-amber-900 disabled:opacity-50 dark:text-amber-100"
                    >
                      Tentar vincular novamente
                    </button>
                  </div>
                )}

                {pendingKaizenId && (
                  <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/40">
                    <p className="text-[12px] text-amber-800 dark:text-amber-200">
                      O Kaizen foi criado, mas não foi possível vinculá-lo à oportunidade.
                    </p>
                    <button
                      type="button"
                      disabled={linkKaizenMutation.isPending}
                      onClick={() => attachKaizen(pendingKaizenId)}
                      className="mt-2 rounded border border-amber-400 px-2 py-1 text-[11px] font-medium text-amber-900 disabled:opacity-50 dark:text-amber-100"
                    >
                      Tentar vincular novamente
                    </button>
                  </div>
                )}

                {opportunity.status === 'approved' && (
                  <div className="space-y-2 pt-1">
                    <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Desdobramento</h3>
                    <button
                      type="button"
                      onClick={() => {
                        setUnfoldOpen((open) => !open);
                        setActionStep(false);
                        setKaizenStep(false);
                        setProjectFormOpen(false);
                      }}
                      className="rounded bg-blue-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-700"
                    >
                      + Desdobrar oportunidade
                    </button>
                    {unfoldOpen && (
                      <div className="space-y-2 rounded border border-slate-200 p-2 dark:border-slate-700">
                        <button
                          type="button"
                          onClick={() => {
                            setActionStep(true);
                            setKaizenStep(false);
                            setProjectFormOpen(false);
                          }}
                          className="block w-full rounded border border-slate-200 px-2.5 py-2 text-left hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-800"
                        >
                          <span className="block text-[12px] font-semibold text-slate-800 dark:text-slate-100">Ação</span>
                          <span className="block text-[11px] text-slate-500">Solução pontual que pode ser executada diretamente.</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setKaizenStep(true);
                            setActionStep(false);
                            setProjectFormOpen(false);
                          }}
                          className="block w-full rounded border border-slate-200 px-2.5 py-2 text-left hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-800"
                        >
                          <span className="block text-[12px] font-semibold text-slate-800 dark:text-slate-100">Kaizen</span>
                          <span className="block text-[11px] text-slate-500">Melhoria estruturada de processo, com meta, implementação e acompanhamento de resultado.</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setActionStep(false);
                            setKaizenStep(false);
                            setProjectName(opportunity.title);
                            setProjectDescription(opportunity.description ?? '');
                            setProjectFormOpen(true);
                          }}
                          className="block w-full rounded border border-slate-200 px-2.5 py-2 text-left hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-800"
                        >
                          <span className="block text-[12px] font-semibold text-slate-800 dark:text-slate-100">Projeto</span>
                          <span className="block text-[11px] text-slate-500">Iniciativa que coordena escopo, pessoas, etapas e múltiplas ações ou análises.</span>
                        </button>
                        {actionStep && (
                          projectsQuery.isLoading ? (
                            <p className="text-[12px] text-slate-400">Carregando projetos...</p>
                          ) : visibleProjects.length === 0 ? (
                            <div className="space-y-2">
                              <p className="text-[12px] text-slate-600 dark:text-slate-300">
                                Para criar uma ação, selecione ou crie primeiro um projeto.
                              </p>
                              <button
                                type="button"
                                onClick={() => navigate('/projetos')}
                                className="text-[11px] font-medium text-blue-600"
                              >
                                Abrir Projetos
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <label className={labelClass}>Projeto</label>
                              <select
                                value={selectedProjectId}
                                onChange={(event) => setSelectedProjectId(event.target.value)}
                                className={modalSelectClass}
                              >
                                <option value="">Selecione um projeto</option>
                                {visibleProjects.map((project) => (
                                  <option key={project.id} value={project.id}>{project.name}</option>
                                ))}
                              </select>
                              <button
                                type="button"
                                disabled={!selectedProjectId}
                                onClick={() => setActionModalOpen(true)}
                                className="rounded bg-blue-600 px-2.5 py-1.5 text-[11px] font-semibold text-white disabled:opacity-50"
                              >
                                Continuar
                              </button>
                            </div>
                          )
                        )}
                        {kaizenStep && (
                          projectsQuery.isLoading ? (
                            <p className="text-[12px] text-slate-400">Carregando projetos...</p>
                          ) : visibleProjects.length === 0 ? (
                            <div className="space-y-2">
                              <p className="text-[12px] text-slate-600 dark:text-slate-300">
                                Para criar um Kaizen, selecione ou crie primeiro um projeto.
                              </p>
                              <button
                                type="button"
                                onClick={() => navigate('/projetos')}
                                className="text-[11px] font-medium text-blue-600"
                              >
                                Abrir Projetos
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <label className={labelClass}>Projeto</label>
                              <select
                                value={selectedProjectId}
                                onChange={(event) => setSelectedProjectId(event.target.value)}
                                className={modalSelectClass}
                              >
                                <option value="">Selecione um projeto</option>
                                {visibleProjects.map((project) => (
                                  <option key={project.id} value={project.id}>{project.name}</option>
                                ))}
                              </select>
                              <button
                                type="button"
                                disabled={!selectedProjectId}
                                onClick={() => setKaizenFormOpen(true)}
                                className="rounded bg-blue-600 px-2.5 py-1.5 text-[11px] font-semibold text-white disabled:opacity-50"
                              >
                                Continuar
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        {opportunity && (
          <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
            <button
              type="button"
              disabled={pending}
              onClick={() => setConfirmDelete(true)}
              className="text-[11px] font-medium text-rose-600 hover:text-rose-700 disabled:opacity-50"
            >
              Excluir
            </button>
            {!readOnly && (
              <button
                type="button"
                disabled={pending || !draft?.title.trim()}
                onClick={handleSave}
                className="rounded bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Salvar
              </button>
            )}
          </div>
        )}
      </aside>

      <EnterpriseModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Excluir oportunidade"
        subtitle="A oportunidade será removida. Problemas, ações, projetos e indicadores não são alterados."
        loading={deleteMutation.isPending}
        footer={
          <>
            <ModalSecondaryButton onClick={() => setConfirmDelete(false)} disabled={deleteMutation.isPending}>
              Cancelar
            </ModalSecondaryButton>
            <ModalPrimaryButton onClick={handleDelete} loading={deleteMutation.isPending}>
              Excluir oportunidade
            </ModalPrimaryButton>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {opportunity ? `“${opportunity.title}” deixa de aparecer na lista.` : 'Confirme a exclusão.'}
        </p>
      </EnterpriseModal>

      <EnterpriseModal
        open={Boolean(unlinkTarget)}
        onClose={() => setUnlinkTarget(null)}
        title="Remover vínculo"
        loading={unlinkMutation.isPending}
        footer={
          <>
            <ModalSecondaryButton onClick={() => setUnlinkTarget(null)} disabled={unlinkMutation.isPending}>
              Cancelar
            </ModalSecondaryButton>
            <ModalPrimaryButton onClick={handleUnlink} loading={unlinkMutation.isPending}>
              Remover vínculo
            </ModalPrimaryButton>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {unlinkTarget?.kind === 'project' ? (
            <>
              Remover o vínculo deste projeto com a oportunidade?
              <span className="mt-2 block">O projeto continuará existindo no DATAFLOW.</span>
            </>
          ) : unlinkTarget?.kind === 'kaizen' ? (
            <>
              Remover o vínculo deste Kaizen com a oportunidade?
              <span className="mt-2 block">O Kaizen continuará existindo no DATAFLOW.</span>
            </>
          ) : 'Remover o vínculo desta ação com a oportunidade? A ação continuará existindo no DATAFLOW.'}
        </p>
      </EnterpriseModal>

      {opportunity && (
        <>
        <NewActionModal
          open={actionModalOpen}
          onClose={() => setActionModalOpen(false)}
          organizationId={organizationId}
          planProjectId={selectedProjectId}
          requireProject
          defaultTitle={opportunity.title}
          defaultDescription={opportunity.description ?? ''}
          defaultAssignedTo={opportunity.owner_id ?? undefined}
          orgMembers={members}
          enterpriseProjects={visibleProjects}
          showToast={(type, message) => {
            if (type === 'error') onError(message);
          }}
          onCreated={(created) => {
            if (created?.id) void attachAction(created.id);
          }}
        />
        {kaizenFormOpen && (
          <KaizenForm
            projectId={selectedProjectId}
            organizationId={organizationId}
            defaultTitle={opportunity.title}
            defaultDescription={opportunity.description ?? ''}
            onCancel={() => setKaizenFormOpen(false)}
            onSuccess={(created) => {
              setKaizenFormOpen(false);
              if (created?.id) void attachKaizen(created.id);
            }}
          />
        )}
        </>
      )}

      <EnterpriseModal
        open={projectFormOpen}
        onClose={() => setProjectFormOpen(false)}
        title="Novo projeto"
        subtitle="A oportunidade permanece aprovada. O projeto nasce pelo cadastro já existente."
        loading={creatingProject}
        footer={
          <>
            <ModalSecondaryButton onClick={() => setProjectFormOpen(false)} disabled={creatingProject}>
              Cancelar
            </ModalSecondaryButton>
            <ModalPrimaryButton onClick={handleCreateProject} disabled={!projectName.trim()} loading={creatingProject}>
              Criar projeto
            </ModalPrimaryButton>
          </>
        }
      >
        <label className={labelClass}>Nome</label>
        <input
          value={projectName}
          onChange={(event) => setProjectName(event.target.value)}
          className={modalInputClass}
          autoFocus
        />
        <label className={`${labelClass} mt-3`}>Descrição</label>
        <textarea
          value={projectDescription}
          onChange={(event) => setProjectDescription(event.target.value)}
          className={`${modalInputClass} min-h-[72px] resize-y`}
          rows={3}
        />
      </EnterpriseModal>
    </div>
  );
};
